import axios from 'axios';
import { Config } from 'config';
import { getAccessToken } from 'lib/Helper/Auth';
import {
  User,
  DetailedUser,
  PagedDetailedUsers,
  UserGroup,
  Role,
  Permission,
  UserRole,
  UserExtraPermission,
  AuditEntry,
} from 'lib/Model/Permission';

export function fetchUsers(): Promise<User[]> {
  return axios
    .get(`${Config.API_BASE_URL}intranet/permissions/users`, {
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    })
    .then((response) => {
      return response.data.data as User[];
    });
}

export function fetchUsersGroups(): Promise<UserGroup[]> {
  return axios
    .get(`${Config.API_BASE_URL}intranet/permissions/users_groups`, {
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    })
    .then((response) => {
      return response.data.data as UserGroup[];
    });
}

export function fetchDetailedUsers(
  pageNumber: number,
  itemsPerPage: number,
  googleId?: string,
  team?: string,
  name?: string,
  role?: string,
  permission?: string,
): Promise<PagedDetailedUsers> {
  return axios
    .get(`${Config.API_BASE_URL}intranet/permissions/users/detailed`, {
      params: {
        page: pageNumber,
        page_size: itemsPerPage,
        google_id: googleId,
        team: team,
        name: name,
        role: role,
        permission: permission,
      },
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    })
    .then((response) => {
      return {
        data: response.data.data as DetailedUser[],
        total_count: response.data.total_count,
      };
    });
}

export function fetchRoles(): Promise<Role[]> {
  return axios
    .get(`${Config.API_BASE_URL}intranet/permissions/roles`, {
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    })
    .then((response) => {
      return response.data.data as Role[];
    });
}

export function fetchPermissions(q?: string): Promise<Permission[]> {
  return axios
    .get(`${Config.API_BASE_URL}intranet/permissions`, {
      params: { q },
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    })
    .then((response) => {
      return response.data.data as Permission[];
    });
}

export function fetchUserRoles(googleId: string): Promise<UserRole[]> {
  return axios
    .get(`${Config.API_BASE_URL}intranet/permissions/users/${googleId}/roles`, {
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    })
    .then((response) => {
      return response.data.data as UserRole[];
    });
}

export function fetchUserExtraPermissions(
  googleId: string,
): Promise<UserExtraPermission[]> {
  return axios
    .get(
      `${Config.API_BASE_URL}intranet/permissions/users/${googleId}/extras`,
      {
        responseType: 'json',
        headers: {
          'Content-Type': 'application/json',
          'access-token': getAccessToken(),
        },
      },
    )
    .then((response) => {
      return response.data.data as UserExtraPermission[];
    });
}

export function fetchUserEffectivePermissions(
  googleId: string,
): Promise<string[]> {
  return axios
    .get(`${Config.API_BASE_URL}intranet/permissions/users/${googleId}`, {
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    })
    .then((response) => {
      return response.data.data as string[];
    });
}

export function assignRoleToUser(
  googleId: string,
  roleSlug: string,
): Promise<void> {
  return axios
    .post(
      `${Config.API_BASE_URL}intranet/permissions/users/${googleId}/roles`,
      { roleSlug },
      {
        responseType: 'json',
        headers: {
          'Content-Type': 'application/json',
          'access-token': getAccessToken(),
        },
      },
    )
    .then(() => {});
}

export function removeRoleFromUser(
  googleId: string,
  roleSlug: string,
): Promise<void> {
  return axios
    .delete(
      `${Config.API_BASE_URL}intranet/permissions/users/${googleId}/roles/${roleSlug}`,
      {
        responseType: 'json',
        headers: {
          'Content-Type': 'application/json',
          'access-token': getAccessToken(),
        },
      },
    )
    .then(() => {});
}

export function grantExtraPermission(
  googleId: string,
  permissionSlug: string,
  note?: string,
): Promise<void> {
  return axios
    .post(
      `${Config.API_BASE_URL}intranet/permissions/users/${googleId}/extras`,
      { permissionSlug, note },
      {
        responseType: 'json',
        headers: {
          'Content-Type': 'application/json',
          'access-token': getAccessToken(),
        },
      },
    )
    .then(() => {});
}

export function revokeExtraPermission(
  googleId: string,
  permissionSlug: string,
): Promise<void> {
  return axios
    .delete(
      `${Config.API_BASE_URL}intranet/permissions/users/${googleId}/extras/${permissionSlug}`,
      {
        responseType: 'json',
        headers: {
          'Content-Type': 'application/json',
          'access-token': getAccessToken(),
        },
      },
    )
    .then(() => {});
}

export function fetchAudit(
  pageNumber: number,
  itemsPerPage: number,
  actor?: string,
  target?: string,
  action?: string,
  subjectSlug?: string,
): Promise<{ data: AuditEntry[]; total_count: number }> {
  return axios
    .get(`${Config.API_BASE_URL}intranet/permissions/audit`, {
      params: {
        page: pageNumber,
        page_size: itemsPerPage,
        actor,
        target,
        action,
        subject_slug: subjectSlug,
      },
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    })
    .then((response) => {
      return {
        data: response.data.data as AuditEntry[],
        total_count: response.data.total_count,
      };
    });
}
